import {ApplicationRef, Component, inject, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Subject, take, takeUntil, tap} from 'rxjs';
import {Nestable} from "@core/model/summary";
import {MapConfigSelector} from "@core/ngrx/selectors/map-config.selector";
import {Store} from "@ngrx/store";
import {mapConfigActions, providersActions, datacenterActions} from "@core/ngrx/actions";
import * as _ from "lodash";
import {Actions, ofType} from "@ngrx/effects";
import {MapDisplayComponent} from "@shared/map-display/map-display.component";
import {Provider} from "@core/model/providers";
import {ProvidersSelector} from "@core/ngrx/selectors/providers.selector";
import {DatacenterV2} from "@core/model/datacenter-v2";
import * as ProviderActions from "@core/ngrx/actions/provider.actions";
import {ProvidersFacade} from "@core/ngrx/facades/providers.facade";
import {ProviderService} from "@core/service/provider.service";

@Component({
  selector: 'app-providers-table-aux',
  templateUrl: './providers-table.component.html',
  styleUrls: ['./providers-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ProvidersTableAuxComponent implements OnInit, OnDestroy {
  private providersFacade: ProvidersFacade = inject(ProvidersFacade);
  private providersService: ProviderService = inject(ProviderService);

  @Input()
  onlyVisible: boolean = true;
  public providers: Provider[] = [];
  public providersOrig: Provider[] = [];
  public providersInView: Provider[] = [];
  public currentFilter: string = "";
  public changingColorProvider: Provider = null;
  public changingColor: string = null;

  public selectedProviders: Provider[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  public isSearching: boolean = false;

  protected globalProviders: Provider[] = [];
  private map: any;


  constructor(private mapConfigSelector: MapConfigSelector,
              private readonly store: Store,
              private actions$: Actions,
              private appRef: ApplicationRef,
              //private mapDisplay: MapDisplayComponent,
              private providersSelector: ProvidersSelector) {
    //this.map = this.mapDisplay.map;
  }

  ngOnInit(): void {
    this.providersFacade.listenToLoadProvidersConfiguration$().subscribe({
      next: (data: any) => {
        this.globalProviders = data.globalProviders;
        const processedProviders: Provider[] = this.providersService.processProvidersWithGlobalProviders(data.localProviders, data.globalProviders);
        this.handleProvidersChange(processedProviders);
      },
      error: () => {
        console.log("")
      }
    });

    this.actions$.pipe(
      ofType(mapConfigActions.MODIFIED_CENTER),
      takeUntil(this.destroy$),
      tap(() => {
        this.map.easeTo({
          zoom: this.map.getZoom(),
          duration: 50,
          essential: true,
          easing: (t) => t,
        });
      })
    ).subscribe();
  }


  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  filter(dt, event) {
    this.currentFilter = event.target.value.toUpperCase();
    this.isSearching = this.currentFilter.length > 0;
    if (this.currentFilter) {
      this.providers = this.providersInView.filter(p => p.name.toUpperCase().indexOf(this.currentFilter) != -1);
    } else {
      this.providers = this.providersInView;
    }
    this.providers = this.providers.filter(p => p.name.toUpperCase().indexOf(event.target.value.toUpperCase()) != -1);
    this.handleSelectedAfterFilter();
  }

  handleSelectedAfterFilter() {
    const selectedAfterFilter: Nestable[] = [];
    for (const currentSelected of this.selectedProviders) {
      if (this.providers.find(p => p.name == currentSelected.name)) {
        selectedAfterFilter.push(currentSelected);
      }
    }
  }

  handleProvidersChange(providers: Provider[]) {
    // Create deep copies to avoid unwanted mutations
    const selectedProviders = _.cloneDeep(this.selectedProviders);
    const newProviders = _.cloneDeep(providers);
    newProviders.map(p => p.hasChanged = false);

    // Update existing selected providers with new information
    newProviders.forEach(newProvider => {
      const existingProvider = selectedProviders.find(
        selectedProvider => selectedProvider.name === newProvider.name
      );

      if (existingProvider) {
        Object.assign(existingProvider, newProvider);
      }
    });

    // Filter out selected providers that no longer exist in the new list
    const updatedSelectedProviders = selectedProviders.filter(selectedProvider =>
      newProviders.some(newProvider => newProvider.name === selectedProvider.name)
    );

    // Update state
    this.selectedProviders = updatedSelectedProviders;

    // Sort providers alphabetically
    this.providers = newProviders.sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase())
    );

    // Keep original copy of providers
    this.providersOrig = [...this.providers];

    if (this.currentFilter) {
      this.providersInView = this.providers.filter(p =>
        p.name.toUpperCase().includes(this.currentFilter)
      );
    } else {
      this.providersInView = this.providers;
    }
  }


  selectedProvider(event) {
    const selectedProvider = event.data as Provider;
    this.mapConfigSelector.getUserMap().pipe(
      take(1),
      tap(map => {
        this.store.dispatch(datacenterActions.getDatacentersByProvider({
          provider: selectedProvider,
          mapId: map._id
        }));
      })
    ).subscribe();
  }

  unselectedProvider(event) {
    const unselectedProvider = event.data as Provider;
    this.mapConfigSelector.getUserMap().pipe(
      take(1),
      tap(map => {
        this.store.dispatch(providersActions.providerUnselected({
          provider: unselectedProvider
        }));
      })
    ).subscribe();
  }

  hideProvider(providers: Provider[], visible: boolean) {
    this.dispatchVisibilityChange(providers, visible);
    if (this.currentFilter) {
      this.updateLocalProvidersState(providers, visible);
    }
  }

  private dispatchVisibilityChange(providers: Provider[], visible: boolean): void {
    const toChange = _.cloneDeep(providers)
    toChange.forEach(p => p.hasChanged = false);
    this.store.dispatch(providersActions.toggleVisibility({
      providers: toChange,
      isVisible: !visible
    }));
  }

  private updateLocalProvidersState(providers: Provider[], visible: boolean): void {
    this.providersInView = this.updateProvidersVisibility(
      this.providersInView,
      providers,
      visible
    );

    // Apply current filter to updated providers if needed
    if (this.currentFilter) {
      this.providers = this.filterProvidersByCurrentSearch(this.providersInView);
    }
  }

  private updateProvidersVisibility(
    currentProviders: Provider[],
    providersToUpdate: Provider[],
    visible: boolean
  ): Provider[] {
    return currentProviders.map(provider => {
      const shouldUpdate = providersToUpdate.some(p => p.name === provider.name);
      if (shouldUpdate) {
        return {
          ...provider,
          isProviderHidden: !visible
        };
      }
      return provider;
    });
  }

  private filterProvidersByCurrentSearch(providers: Provider[]): Provider[] {
    return providers.filter(provider =>
      provider.name.toUpperCase().indexOf(this.currentFilter) !== -1
    );
  }

  private handledHighlited(provider: Provider, isHighlight: boolean) {
    this.store.dispatch(mapConfigActions.datacenterHoverChange({
      hover: isHighlight,
      value: provider.name,
      isProvider: true
    }));
  }

  colorChanged(event, provider) {
    this.changingColorProvider = provider;
    this.changingColor = event.value;
  }

  onShow() {
    this.appRef.tick();
  }

  onHide() {
    if (this.changingColorProvider && this.changingColor) {
      this.store.dispatch(providersActions.providerColorChanged({
        provider: this.changingColorProvider
      }));
      this.changingColorProvider = null;
      this.changingColor = null;
    }

  }

  get allProvidersHidden(): boolean {
    return this.providers.every(p => !p.isVisible);
  }

  toggleAll() {
    const newVisibility = !this.allProvidersHidden;
    const updatedProviders = _.cloneDeep(this.providersOrig);
    this.hideProvider(updatedProviders, newVisibility);
  }

  onImageSelected(event: any, provider: Provider) {
    const toSend = _.cloneDeep(provider);
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const imageBase64 = e.target.result;
        toSend.base64Icon = imageBase64;
        toSend.hasChanged = true;
        toSend.isGlobal = false;
        this.store.dispatch(providersActions.providersModified({
          provider: toSend
        }));

      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(provider: Provider) {
    const toSend = _.cloneDeep(provider);
    toSend.base64Icon = '';
    toSend.hasChanged = true;
    this.store.dispatch(providersActions.providersModified({
      provider: toSend
    }));

  }

  isTemporaryProvider(provider: Provider): boolean {
    return provider.id && provider.id.startsWith('AUX-');
  }

}
